import {
  AsyncPipe,
  DatePipe,
  FormStyle,
  getLocaleDayNames,
  NgClass,
  NgFor,
  NgIf,
  SlicePipe,
  TitleCasePipe,
  TranslationWidth,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Appointment,
  LocationData,
  LocationDataGeocoded,
} from '@sc/data-access-models';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { AppointmentsSubService } from '@sc/feature-appointments-subscription';
import { arrayMoveElementFromTo } from '@sc/utils';
import { IconComponent } from '../icon/icon.component';
import { CalendarAppointmentsPanelComponent } from './calendar-appointments-panel/calendar-appointments-panel.component';

@Component({
  selector: 'sc-calendar',
  styleUrls: ['./calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    IconComponent,
    NgFor,
    NgClass,
    CalendarAppointmentsPanelComponent,
    AsyncPipe,
    SlicePipe,
    TitleCasePipe,
    DatePipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <div class="month-selector">
      <sc-icon
        name="arrow_left"
        id="calendarMonthDecrementButton"
        tabindex="0"
        role="button"
        [attr.aria-label]="
          'GlobalApplicativeConstants.accessibility.labels.previous' | translate
        "
        (click)="incrementDisplayedMonth(-1)"
        (keydown.enter)="incrementDisplayedMonth(-1)"
        (keydown.space)="incrementDisplayedMonth(-1)"
        *ngIf="(currentDate | date: 'M') < (displayedMonth | date: 'M')"
      ></sc-icon>

      <span>{{ displayedMonth | localeDate: 'MMMM - yy' | async }}</span>
      <sc-icon
        id="calendarMonthIncrementButton"
        class="arrow-right"
        name="arrow_right"
        tabindex="0"
        role="button"
        [attr.aria-label]="
          'GlobalApplicativeConstants.accessibility.labels.next' | translate
        "
        (click)="incrementDisplayedMonth()"
        (keydown.enter)="incrementDisplayedMonth()"
        (keydown.space)="incrementDisplayedMonth()"
      ></sc-icon>
    </div>

    <div class="calendar">
      <div class="header">
        <div
          class="day-name"
          aria-hidden="true"
          *ngFor="let dayName of daysName"
        >
          {{ dayName | titlecase | slice: 0:3 }}
        </div>
      </div>

      <div class="body">
        <div class="week" *ngFor="let weekNumber of weeksTable">
          <div
            class="day"
            *ngFor="let dayNumber of weekDaysTable"
            [ngClass]="{
              current: isCurrent(weekNumber, dayNumber),
              selected: isSelected(weekNumber, dayNumber),
              holiday: isHolidayDay(weekNumber, dayNumber),
              out: isOut(weekNumber, dayNumber),
              disabled: isDisabled(weekNumber, dayNumber)
            }"
          >
            <div
              [class]="
                'day-container ' +
                weekNumber +
                'Week' +
                dayNumber +
                'DayCalendarSlot'
              "
              [attr.tabindex]="
                getAvailability(weekNumber, dayNumber) ? '0' : '-1'
              "
              [attr.role]="
                getAvailability(weekNumber, dayNumber)
                  ? 'button'
                  : 'presentation'
              "
              [attr.aria-label]="
                'GlobalApplicativeConstants.accessibility.labels.calendarDay'
                  | translate
                    : {
                        dayName:
                          getDateByWeekAndDay(weekNumber, dayNumber)
                          | localeDate: 'fullDate'
                          | async,
                        nbApmt: getAvailability(weekNumber, dayNumber)
                      }
              "
              (click)="onSelectDate(weekNumber, dayNumber)"
              (keydown.enter)="onSelectDate(weekNumber, dayNumber)"
              (keydown.space)="onSelectDate(weekNumber, dayNumber)"
            >
              <div class="day-number" aria-hidden="true">
                <div class="day-number-label">
                  {{
                    getDateByWeekAndDay(weekNumber, dayNumber)
                      | localeDate: 'd'
                      | async
                  }}
                </div>
                <span
                  style="flex-grow: 1; flex-shrink: 1; flex-basis: auto;"
                ></span>
                <span *ngIf="highlightBestScoreFitter">
                  <span
                    class="icon"
                    *ngIf="
                      getDateByWeekAndDay(
                        weekNumber,
                        dayNumber
                      ).toDateString() === closestFitterAppointmentDateKey
                    "
                  >
                    🎉
                  </span>
                </span>
              </div>

              <div
                class="availability"
                aria-hidden="true"
                *ngIf="getAvailability(weekNumber, dayNumber)"
              >
                <sc-icon name="book_appointment"></sc-icon>
                <div class="number sc-design-title-h1">
                  {{ getAvailability(weekNumber, dayNumber) }}
                </div>
                <span
                  class="replacement-vehicle-icon"
                  *ngIf="getDayWithReplacementVehicle(weekNumber, dayNumber)"
                >
                  <sc-icon
                    name="car"
                    specificName="replacement_vehicle"
                    [forceInitialColor]="true"
                  ></sc-icon>
                </span>
              </div>
            </div>

            <sc-calendar-appointments-panel
              class="appointments-panel"
              [ngClass]="{
                left: dayNumber > 2,
                top: weekNumber > 2
              }"
              *ngIf="
                withAppointmentSelection &&
                isOpenedAppointmentsPanel(weekNumber, dayNumber)
              "
              [date]="getDateByWeekAndDay(weekNumber, dayNumber)"
              [appointments]="getAppointmentsByDay(weekNumber, dayNumber)"
              [disabledReplacementVehicle]="disabledReplacementVehicle"
              [existingAppointment]="existingAppointment"
              (selectAppointment)="onSelectAppointment($event)"
              (closeClick)="onCloseAppointmentPanel()"
              [defaultDuration]="defaultDuration"
              [suggestedLocationData]="suggestedLocationData"
              [selectedGeocodedAddress]="selectedGeocodedAddress"
              (addressSelect)="onAddressSelect($event)"
              (addressChange)="onAddressStr($event)"
              (lightAptValidetEvent)="onLightAptValidete($event)"
              [canCreateLightAppointment]="canCreateLightAppointment"
            ></sc-calendar-appointments-panel>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class CalendarComponent implements OnInit {
  @Input() withAppointmentSelection = false;
  @Input() disabledReplacementVehicle: boolean;
  @Input() closestFitterAppointmentDateKey: string;
  @Input() defaultDuration: string;
  @Input() public highlightBestScoreFitter = false;
  @Input() appointmentSubscription: AppointmentsSubService;
  @Input() existingAppointment: Appointment;
  @Input() suggestedLocationData: LocationData[];
  @Input() selectedGeocodedAddress: LocationDataGeocoded;
  @Input() canCreateLightAppointment;

  @Output() addressSelect = new EventEmitter<LocationData>();
  @Output() addressChange = new EventEmitter<string>();

  @Output() selectDate = new EventEmitter<Date>();
  @Output() selectAppointment = new EventEmitter<Appointment>();
  @Output() lightAptValidetEvent = new EventEmitter<Appointment>();

  daysName: string[];
  weeksTable = [0, 1, 2, 3, 4];
  weekDaysTable = [0, 1, 2, 3, 4, 5, 6];

  currentDate = new Date();
  selectedDate: Date;

  calendarAppointments = new Map<string, number>();
  appointmentsByDay = new Map<string, Appointment[]>();

  days: Date[];

  openedAppointmentsPanel = false;
  displayNextMonth = false;

  displayedMonth = new Date(
    this.currentDate.getFullYear(),
    this.currentDate.getMonth()
  );

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public translatorService: TranslatorService,
    public configurationService: ConfigurationService
  ) {}

  ngOnInit(): void {
    this.translatorService.getCurrentLang().subscribe((newFormattedLang) => {
      const days = getLocaleDayNames(
        newFormattedLang,
        FormStyle.Standalone,
        TranslationWidth.Wide
      ).slice();
      this.daysName = arrayMoveElementFromTo(days.slice(), 0, days.length);
    });

    this.translatorService.setDefaultLang(
      this.configurationService.getConfig().defaultLang
    );
    this.currentDate.setHours(0, 0, 0, 0);
    this.days = this.generateMonthDays(this.displayedMonth);

    if (this.displayNextMonth) {
      this.incrementDisplayedMonth();
    }

    this.appointmentSubscription.calendarAppointments$.subscribe((ca) => {
      this.calendarAppointments = ca;
      this.changeDetectorRef.detectChanges();
    });

    this.appointmentSubscription.appointmentsByDay$.subscribe((abd) => {
      this.appointmentsByDay = abd;
      this.changeDetectorRef.detectChanges();
    });
  }

  incrementDisplayedMonth(value = 1) {
    this.displayedMonth = new Date(
      this.displayedMonth.getFullYear(),
      this.displayedMonth.getMonth() + value
    );

    this.days = this.generateMonthDays(this.displayedMonth);

    this.changeDetectorRef.detectChanges();
  }

  generateMonthDays(month: Date): Date[] {
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const numberOfDaysInMonth = lastDay.getDate();
    const numberOfDaysInPreviousMonth = (firstDay.getDay() + 6) % 7;
    const numberOfDaysInNextMonth =
      35 - numberOfDaysInPreviousMonth + numberOfDaysInMonth;

    const days = [];

    for (let i = -numberOfDaysInPreviousMonth + 1; i <= 0; i++) {
      days.push(new Date(month.getFullYear(), month.getMonth(), i));
    }

    for (let i = 1; i <= numberOfDaysInMonth; i++) {
      days.push(new Date(month.getFullYear(), month.getMonth(), i));
    }

    for (let i = 1; i <= numberOfDaysInNextMonth; i++) {
      days.push(
        new Date(month.getFullYear(), month.getMonth(), numberOfDaysInMonth + i)
      );
    }
    const today = new Date();
    if (
      today.getDate() - (numberOfDaysInMonth - numberOfDaysInPreviousMonth) >
      0
    ) {
      this.displayNextMonth = true;
    }

    return days;
  }

  getDateByWeekAndDay(weekNumber: number, dayNumber: number) {
    return this.days[weekNumber * 7 + dayNumber];
  }

  getAvailability(weekNumber: number, dayNumber: number) {
    const availabilityDate = this.getDateByWeekAndDay(weekNumber, dayNumber);

    return this.calendarAppointments
      ? this.calendarAppointments.get(availabilityDate.toISOString()) || 0
      : 0;
  }

  getAppointmentsByDay(weekNumber: number, dayNumber: number) {
    const day = this.getDateByWeekAndDay(weekNumber, dayNumber);

    return this.appointmentsByDay.get(day.toISOString());
  }

  getDayWithReplacementVehicle(weekNumber: number, dayNumber: number) {
    const appointments = this.getAppointmentsByDay(weekNumber, dayNumber);
    if (appointments) {
      if (appointments.some((appointment) => appointment.replacementVehicle)) {
        return true;
      }
      return false;
    }
  }

  isCurrent(weekNumber: number, dayNumber: number) {
    const day = this.getDateByWeekAndDay(weekNumber, dayNumber);

    return day.getTime() === this.currentDate.getTime();
  }

  isSelected(weekNumber: number, dayNumber: number) {
    const day = this.getDateByWeekAndDay(weekNumber, dayNumber);

    return this.selectedDate
      ? day.getTime() === this.selectedDate.getTime()
      : false;
  }

  isHolidayDay(weekNumber: number, dayNumber: number) {
    const day = this.getDateByWeekAndDay(weekNumber, dayNumber);

    return day.getDay() === 0;
  }

  isOut(weekNumber: number, dayNumber: number) {
    const day = this.getDateByWeekAndDay(weekNumber, dayNumber);

    return day.getMonth() !== this.displayedMonth.getMonth();
  }

  isDisabled(weekNumber: number, dayNumber: number) {
    return !this.getAvailability(weekNumber, dayNumber);
  }

  isOpenedAppointmentsPanel(weekNumber: number, dayNumber: number) {
    return (
      this.isSelected(weekNumber, dayNumber) && this.openedAppointmentsPanel
    );
  }

  onSelectDate(weekNumber: number, dayNumber: number) {
    if (this.isDisabled(weekNumber, dayNumber)) {
      return;
    }

    const newSelectedDate = new Date(
      this.getDateByWeekAndDay(weekNumber, dayNumber).getTime()
    );

    this.openedAppointmentsPanel =
      newSelectedDate.getTime() !== this.selectedDate?.getTime() ||
      !this.openedAppointmentsPanel;

    this.selectedDate = newSelectedDate;

    this.selectDate.emit(this.selectedDate);
    this.changeDetectorRef.detectChanges();
  }

  onCloseAppointmentPanel() {
    this.openedAppointmentsPanel = false;
  }

  onSelectAppointment(appointment: Appointment) {
    this.selectAppointment.emit(appointment);
    this.onCloseAppointmentPanel();
  }

  onAddressSelect($event) {
    this.addressSelect.emit($event);
  }
  onAddressStr($event) {
    this.addressChange.emit($event);
  }

  onLightAptValidete(value) {
    this.lightAptValidetEvent.emit(value);
    this.onCloseAppointmentPanel();
  }
}
